import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {ROUTES} from 'src/app/shared/constants/routes';
import {UserProfile} from 'src/app/shared/models/user-profile';
import {ConfigService} from 'src/app/shared/services/config.service';
import {ApplicationProjectService} from './core/application-project/services/application-project.service';
import {IApplication} from './shared/models/application';
import {IUser, User} from './shared/models/user';
import {AuthenticationService} from './shared/services/authentication.service';
import {CurrentUserService} from './shared/services/current-user.service';
import {Constants} from './shared/constants/Constants';
import {ThemeService} from './shared/services/theme.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UsersService} from "./core/user/services/users.service";
import {STEPPER_GLOBAL_OPTIONS} from "@angular/cdk/stepper";
import { SnackbarService } from './shared/services/snackbar.service';
import {AuthRoleRequest, LoginRequest} from './shared/models/auth';

@Component({
  selector: 'no-auth-access',
  styleUrls: ['./no-authentication-access.component.less'],
  templateUrl: './no-authentication-access.component.html',
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: {showError: true}
  }]
})
export class NoAuthenticationAccessComponent implements OnInit {

  identifierFormGroup!: UntypedFormGroup;
  passwordFormGroup!: UntypedFormGroup;

  public navbarColor = "";

  public profile: UserProfile = UserProfile.ADMIN;

  public selectedApplicationsNames: string[] = [];

  public applicationsNames: string[] = [];
  public identifier = '';

  public userAllowed: IUser | undefined;

  canAccess = false;

  isAuthenticating = false;
  accountLocked = false;
  lockMessage = '';

  get UserProfile() {
    return UserProfile;
  }

  get Object() {
    return Object;
  }

  @Output() onUserFound = new EventEmitter<boolean>();

  constructor(public router: Router,
              private configService: ConfigService,
              public theme: ThemeService,
              private applicationProjectService: ApplicationProjectService,
              private authenticationService: AuthenticationService,
              private currentUserService: CurrentUserService,
              private _formBuilder: UntypedFormBuilder,
              private userService: UsersService,
              private snackbarService: SnackbarService,
  ) {
  }

  public ngOnInit() {
    if (this.configService.isAuthEnabled()) {
      this.router.navigateByUrl(`/${ROUTES.CORE_PATH}`);
    } else {
      this.identifierFormGroup = this._formBuilder.group({
        identifier: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(8)]]
      });

     
    }
    this.navbarColor = this.theme.getColor(Constants.PackColor.MAIN_DARK);
  }

  public allowAccess() {
    this.isAuthenticating = true;
    this.accountLocked = false;
    
    const identifier = this.identifierFormGroup.value.identifier.toUpperCase();
    
    // Vérifier d'abord si le compte est verrouillé
    this.userService.isAccountLocked(identifier).subscribe({
      next: response => {
        if (!response.success) {
          // Compte verrouillé
          this.accountLocked = true;
          this.lockMessage = response.message;
          this.isAuthenticating = false;
          this.openErrorSnackBar(response.message);
        } else {
          // Compte actif, vérifier si l'utilisateur existe
          this.checkUserExists(identifier);
        }
      },
      error: error => {
        console.log(error);
        this.isAuthenticating = false;
        this.openErrorSnackBar("Erreur lors de la vérification du compte.");
      }
    });
  }

  private checkUserExists(identifier: string) {
    this.userService.getUserByIdentifier(identifier).subscribe({
      next: user => {
        this.userAllowed = user;
        this.isAuthenticating = false;
      },
      error: error => {
        console.log(error);
        this.isAuthenticating = false;
        if (error.status === 404) {
          this.openErrorSnackBar("Utilisateur non trouvé. Veuillez contacter votre administrateur.");
        } else {
          this.openErrorSnackBar("Vous n'êtes pas autorisé à accéder à l'application. Veuillez contacter votre administrateur.");
        }
      }
    });
  }

  public validateRole() {
    const userRoleRequest: AuthRoleRequest = {
      identifier: this.userAllowed?.identifier ?? '',
      profile: this.profile
    };

    this.authenticationService.setUserRole(userRoleRequest).subscribe({
      next: (response) => {
        if (response.success) {
          this.authenticationService.saveSessionUser(response.user);
          this.authenticationService.setTokenToSession(response.token ? response.token : '');
          this.openSuccessSnackBar("Rôle mis à jour avec succès !");
        } else {
          this.openErrorSnackBar("Échec de la mise à jour du rôle.");
        }
      },
      error: error => {
        console.log(error);
        this.openErrorSnackBar("Erreur lors de la mise à jour du rôle.");
      }
    });

  }

  public authenticateWithPassword() {
    //if (!this.userAllowed) return;

    this.isAuthenticating = true;

    const identifier = this.identifierFormGroup.value.identifier.toUpperCase();
    const password   = this.identifierFormGroup.value.password;

    this.authenticationService.loginWithPassword(identifier, password).subscribe({
        next: (user) => {
           this.userAllowed = user;
            this.isAuthenticating = false;
            this.canAccess = true;
            this.openSuccessSnackBar("Authentification réussie !");

             this.applicationProjectService.getApplicationsNames().subscribe(applicationsNames => {
              this.applicationsNames = applicationsNames;
            });
            
        },
        error: (err) => {
            this.isAuthenticating = false;

            let errorMessage = err.message ?? "Erreur lors de l'authentification.";

            const msgLower = errorMessage.toLowerCase();
            if (msgLower.includes('verrouillé') || msgLower.includes('locked')) {
                this.accountLocked = true;
                this.lockMessage   = errorMessage;
                this.userAllowed   = undefined;
                this.identifierFormGroup.reset();
            }

            this.openErrorSnackBar(errorMessage);
        }
    });
}

  public validateAccess() {
    const user: User = {
      lastName: this.userAllowed?.lastName,
      firstName: this.userAllowed?.firstName,
      identifier: this.userAllowed?.identifier,
      profile: this.profile,
      applications: this.selectedApplicationsNames.map(name => {
        return {name: name} as IApplication;
      }),
      nettingView: this.userAllowed?.nettingView,
    };
    sessionStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('debugMode', 'false');
    this.authenticationService.setUser(user);

    //  Mettre à jour le CurrentUserService (BehaviorSubject) - source de vérité
    this.currentUserService.setCurrentUser({
      id:           this.userAllowed?.id ?? '',
      identifier:   this.userAllowed?.identifier ?? '',
      firstName:    this.userAllowed?.firstName ?? '',
      lastName:     this.userAllowed?.lastName ?? '',
      email:        this.userAllowed?.email ?? '',
      role:         this.profile,
      nettingView:  this.userAllowed?.nettingView ?? false,
      applications: this.selectedApplicationsNames.map(name => ({ name } as IApplication)),
    });

    this.router.navigateByUrl(`/${ROUTES.CORE_PATH}`);
  }

  openErrorSnackBar(message: string) {
    this.snackbarService.openSnackBar(message, 'error-snack-bar');
  }

  openSuccessSnackBar(message: string) {
    this.snackbarService.openSnackBar(message);
  }
}
